import { useState, useEffect, useMemo } from "react";
import { Button } from "./button";
import { Label } from "./label";
import { Textarea } from "./textarea";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "./select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Wand2, Copy, Check } from "lucide-react";

interface JsonLdBuilderProps {
  formData: any;
  availableFields: Array<{
    key: string;
    label: string;
    getValue?: (data: any) => string;
  }>;
  value: string;
  onChange: (value: string) => void;
  pageType?: "article" | "page";
}

export function JsonLdBuilder({
  formData,
  availableFields,
  value,
  onChange,
  pageType = "page",
}: JsonLdBuilderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [template, setTemplate] = useState("");
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(
    {}
  );
  const [fallbackValues, setFallbackValues] = useState<Record<string, string>>(
    {}
  );
  const [copied, setCopied] = useState(false);

  // Default templates cho mỗi loại page
  const defaultTemplates = {
    article: `{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "{{title}}",
  "description": "{{pageDescription}}",
  "author": {
    "@type": "Person",
    "name": "{{authorName}}"
  },
  "datePublished": "{{publishDate}}",
  "dateModified": "{{updatedDate}}",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "{{canonicalUrl}}"
  },
  "keywords": "{{keywords}}",
  "articleBody": "{{content}}"
}`,
    page: `{
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "{{title}}",
  "description": "{{pageDescription}}",
  "url": "{{canonicalUrl}}",
  "datePublished": "{{publishDate}}",
  "dateModified": "{{updatedDate}}",
  "author": {
    "@type": "Person",
    "name": "{{authorName}}"
  },
  "keywords": "{{keywords}}"
}`,
    movie: `{
  "@context": "https://schema.org",
  "@type": "Movie",
  "name": "{{title}}",
  "description": "{{film_description}}",
  "url": "{{canonicalUrl}}",
  "datePublished": "{{publishDate}}",
  "director": {
    "@type": "Person",
    "name": "{{directorName}}"
  },
  "actor": [
    {{actors}}
  ],
  "keywords": "{{keywords}}"
}`,
  };

  // Load template mặc định hoặc từ value hiện tại khi mở
  useEffect(() => {
    if (isOpen && !template) {
      // Nếu có value hiện tại và valid JSON, sử dụng nó
      if (value) {
        try {
          JSON.parse(value);
          setTemplate(value);
        } catch {
          // Nếu value không phải JSON hợp lệ, sử dụng template mặc định
          setTemplate(defaultTemplates[pageType] || defaultTemplates.page);
        }
      } else {
        // Nếu không có value, sử dụng template mặc định
        setTemplate(defaultTemplates[pageType] || defaultTemplates.page);
      }
    }
  }, [isOpen, pageType, template, value]);

  // Extract placeholders từ template
  const placeholders = useMemo(() => {
    if (!template) return [];
    const regex = /\{\{([^}]+)\}\}/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(template)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  }, [template]);

  // Function to highlight JSON with Dracula theme colors
  const highlightJson = (json: string) => {
    try {
      // Parse to validate and format
      const parsed = JSON.parse(json);
      const formatted = JSON.stringify(parsed, null, 2);

      // Apply syntax highlighting
      let highlighted = formatted
        // Highlight property names (keys) - cyan
        .replace(/"([^"]+)":/g, '<span style="color: #8be9fd">"$1"</span>:')
        // Highlight string values - yellow
        .replace(/: "([^"]*)"/g, ': <span style="color: #f1fa8c">"$1"</span>')
        // Highlight numbers - purple
        .replace(/: (\d+\.?\d*)/g, ': <span style="color: #bd93f9">$1</span>')
        // Highlight booleans - pink
        .replace(/: (true|false)/g, ': <span style="color: #ff79c6">$1</span>')
        // Highlight null - pink
        .replace(/: (null)/g, ': <span style="color: #ff79c6">$1</span>')
        // Highlight brackets and braces - white
        .replace(/([{}\[\]])/g, '<span style="color: #f8f8f2">$1</span>')
        // Fix array string values
        .replace(/,\s*"([^"]*)"/g, ', <span style="color: #f1fa8c">"$1"</span>');

      return highlighted;
    } catch {
      // If JSON is invalid, return plain text with error color
      return `<span style="color: #ff5555">${json}</span>`;
    }
  };

  // Build JSON từ template và mappings
  const buildJson = () => {
    let result = template;

    placeholders.forEach((placeholder) => {
      const fieldKey = fieldMappings[placeholder];
      let value: any = "";

      if (fieldKey) {
        const field = availableFields.find((f) => f.key === fieldKey);

        if (field?.getValue) {
          value = field.getValue(formData);
        } else if (fieldKey in formData) {
          value = formData[fieldKey];
        }
      }

      // Sử dụng fallback value nếu field value rỗng hoặc không có field mapping
      if (!value || (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && value.trim() === '')) {
        value = fallbackValues[placeholder] || "";
      }

      // Xử lý các kiểu dữ liệu khác nhau
      let processedValue = "";

      if (value === null || value === undefined) {
        processedValue = "";
      } else if (Array.isArray(value)) {
          // Xử lý array
          if (value.length === 0) {
            processedValue = "[]";
          } else if (typeof value[0] === "object") {
            // Array of objects
            processedValue = JSON.stringify(value);
          } else {
            // Array of primitives - join as string
            processedValue = value.map((v) => String(v)).join(", ");
            // Escape quotes cho string
            processedValue = processedValue
              .replace(/"/g, '\\"')
              .replace(/\n/g, "\\n");
          }
        } else if (typeof value === "object") {
          // Object - stringify it
          processedValue = JSON.stringify(value);
        } else {
          // Primitive value - convert to string
          processedValue = String(value);

          // Kiểm tra xem string có phải là JSON không
          const trimmed = processedValue.trim();
          const isJsonLike =
            (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
            (trimmed.startsWith("{") && trimmed.endsWith("}"));

          if (isJsonLike) {
            // Nếu string có vẻ là JSON, giữ nguyên (có thể là JSON từ getValue)
            // Không escape
          } else {
            // Chỉ escape nếu không phải JSON
            processedValue = processedValue
              .replace(/"/g, '\\"')
              .replace(/\n/g, "\\n");
          }
        }

      result = result.replace(
        new RegExp(`\\{\\{${placeholder}\\}\\}`, "g"),
        processedValue
      );
    });

    // Validate JSON và format
    try {
      const parsed = JSON.parse(result);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return result;
    }
  };

  const handleApply = () => {
    const builtJson = buildJson();
    onChange(builtJson);
    setIsOpen(false);
  };

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(template);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <style>{`
        .dracula-theme::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .dracula-theme::-webkit-scrollbar-track {
          background: #282a36;
          border: 1px solid #44475a;
        }
        .dracula-theme::-webkit-scrollbar-thumb {
          background: #44475a;
          border-radius: 6px;
        }
        .dracula-theme::-webkit-scrollbar-thumb:hover {
          background: #6272a4;
        }
        .dracula-theme::selection {
          background: #44475a;
        }
        .dracula-theme:focus {
          outline: none;
          border-color: #6272a4;
        }
      `}</style>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="gap-2">
            <Wand2 className="h-4 w-4" />
            Tạo từ Template
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-[95vw]! !w-[95vw] !max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>JSON-LD Schema Builder</DialogTitle>
          <DialogDescription>
            Nhập template JSON với placeholder (vd: {"{{title}}"}) và chọn field
            từ form để map vào
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Template selector */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <Label>Chọn template mẫu</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTemplate(defaultTemplates.article)}
                >
                  Article
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTemplate(defaultTemplates.page)}
                >
                  WebPage
                </Button>
                {pageType === "article" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTemplate(defaultTemplates.movie)}
                  >
                    Movie
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Main content with 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column: Template editor and field mappings */}
            <div className="space-y-4">
              {/* Template editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>
                    Template JSON (sử dụng {"{{placeholder}}"} cho các field
                    động)
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyTemplate}
                    className="gap-2"
                  >
                    {copied ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                    {copied ? "Đã copy" : "Copy"}
                  </Button>
                </div>
                <Textarea
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  rows={20}
                  className="font-mono text-xs"
                  placeholder={`{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "{{title}}"
}`}
                />
              </div>

              {/* Field mappings */}
              {placeholders.length > 0 && (
                <div className="space-y-2">
                  <Label>Map placeholder với field từ form</Label>
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
                    {placeholders.map((placeholder) => (
                      <div
                        key={placeholder}
                        className="flex items-center gap-2 p-3 bg-white rounded border"
                      >
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded min-w-[140px]">
                          {"{{"}
                          {placeholder}
                          {"}}"}
                        </span>
                        <span>→</span>
                        <Select
                          value={fieldMappings[placeholder] || "__none__"}
                          onValueChange={(value) =>
                            setFieldMappings({
                              ...fieldMappings,
                              [placeholder]: value === "__none__" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Chọn field từ form" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Không map</SelectItem>
                            {availableFields.map((field) => (
                              <SelectItem key={field.key} value={field.key}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-gray-500">hoặc</span>
                        <Input
                          type="text"
                          placeholder="Giá trị mặc định..."
                          value={fallbackValues[placeholder] || ""}
                          onChange={(e) =>
                            setFallbackValues({
                              ...fallbackValues,
                              [placeholder]: e.target.value,
                            })
                          }
                          className="flex-1 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right column: Preview */}
            <div className="space-y-2">
              <Label>Xem trước JSON-LD được tạo</Label>
              <div className="relative">
                <div
                  className="font-mono text-xs dracula-theme overflow-auto"
                  style={{
                    backgroundColor: '#282a36',
                    color: '#f8f8f2',
                    border: '1px solid #44475a',
                    lineHeight: '1.5',
                    padding: '16px',
                    borderRadius: '6px',
                    height: '600px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    resize: 'vertical'
                  }}
                  dangerouslySetInnerHTML={{ __html: highlightJson(buildJson()) }}
                />
                {(() => {
                  try {
                    JSON.parse(buildJson());
                    return (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ JSON hợp lệ
                      </p>
                    );
                  } catch {
                    return (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠ JSON chưa hoàn chỉnh (kiểm tra lại placeholder chưa
                        được map)
                      </p>
                    );
                  }
                })()}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Hủy
            </Button>
            <Button type="button" onClick={handleApply} disabled={!template}>
              Áp dụng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
